/**
 * ExcelJS export of the current DataGrid view: visible columns (except
 * disableExport) and the rows MUI would include in a normal export (respects
 * active filters / quick filter).
 */
import ExcelJS from "exceljs";
import type { MutableRefObject } from "react";
import type { GridApiPro } from "@mui/x-data-grid-pro";
import { defaultGetRowsToExport } from "@mui/x-data-grid/internals";

export type ExportGridToExcelOptions = {
  readonly sheetName: string;
  readonly getFileName: () => string;
  readonly creator?: string;
};

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function exportGridToExcel(
  apiRef: MutableRefObject<GridApiPro>,
  options: ExportGridToExcelOptions
): Promise<void> {
  const api = apiRef.current;
  if (!api) {
    return;
  }

  // Match what the user sees: hidden and disableExport columns are omitted.
  const columns = api
    .getVisibleColumns()
    .filter((column) => column.disableExport !== true);
  // Uses MUI's export row selection (filtered / quick-filtered set).
  const rowIds = defaultGetRowsToExport({ apiRef });

  const workbook = new ExcelJS.Workbook();
  workbook.creator = options.creator ?? "Rules Authoring Tool";
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(options.sheetName);
  worksheet.columns = columns.map((column) => ({
    header: column.headerName ?? column.field,
    key: column.field,
    // Rough px → Excel character width conversion.
    width: Math.max(
      12,
      Math.round((column.width ?? column.minWidth ?? 120) / 8)
    ),
  }));

  rowIds.forEach((rowId) => {
    const row = api.getRow(rowId);
    if (!row) {
      return;
    }

    // getRowValue applies valueGetters — chip columns export as their string form.
    worksheet.addRow(
      Object.fromEntries(
        columns.map((column) => [column.field, api.getRowValue(row, column)])
      )
    );
  });

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = {
    vertical: "middle",
    horizontal: "center",
    wrapText: true,
  };
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columns.length },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  downloadBlob(blob, options.getFileName());
}

/** e.g. "ProductLine - Table Summary - 2026-07-14-12-30-00.xlsx" */
export function buildTimestampedExcelFileName(
  baseName: string,
  prefix?: string
): string {
  const date = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const fileName = `${baseName} - ${date}.xlsx`;
  const trimmedPrefix = prefix?.trim();

  return trimmedPrefix ? `${trimmedPrefix} - ${fileName}` : fileName;
}
